import { UserSelect } from '@/components/global/UserSelect';
import {
    ResponsiveModal,
    ResponsiveModalBody,
    ResponsiveModalContent,
    ResponsiveModalDescription,
    ResponsiveModalFooter,
    ResponsiveModalHeader,
    ResponsiveModalTitle,
    ResponsiveModalTrigger,
} from '@/components/ui/responsive-modal';
import { Alert, AlertDescription } from '@/components/ui/shadcn/alert';
import {
    AlertDialog,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/shadcn/alert-dialog';
import { Button } from '@/components/ui/shadcn/button';
import { Combobox } from '@/components/ui/shadcn/combobox';
import { DatePicker } from '@/components/ui/shadcn/date-picker';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/shadcn/form';
import { Input } from '@/components/ui/shadcn/input';
import { useSettings } from '@/contexts/SettingsContext';
import { useTranslations } from '@/hooks/useTranslations';
import { taskUpdateDenialReason } from '@/lib/projects/taskDepartmentAccess';
import { settingLabel } from '@/lib/status-labels';
import { ProjectTask } from '@/types';
import { standardSchemaResolver } from '@hookform/resolvers/standard-schema';
import type { RequestPayload } from '@inertiajs/core';
import { router, usePage } from '@inertiajs/react';
import { Loader2, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { type FieldPath, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import * as z from 'zod';

type EditTaskFormValues = {
    name: string;
    start_date: string;
    end_date: string;
    status: string;
    assigned_to: string;
    parent_task_id: string;
    department_key: string;
};

const EDIT_TASK_FORM_FIELDS: (keyof EditTaskFormValues)[] = [
    'name',
    'start_date',
    'end_date',
    'status',
    'assigned_to',
    'parent_task_id',
    'department_key',
];

function applyInertiaValidationErrors(
    errors: Record<string, string | string[]>,
    setError: (name: FieldPath<EditTaskFormValues>, error: { message: string }) => void,
): string | null {
    let firstError: string | null = null;
    for (const [key, raw] of Object.entries(errors)) {
        const message = Array.isArray(raw) ? raw[0] : raw;
        if (!message) {
            continue;
        }
        if (firstError === null) {
            firstError = message;
        }
        if (EDIT_TASK_FORM_FIELDS.includes(key as keyof EditTaskFormValues)) {
            setError(key as FieldPath<EditTaskFormValues>, { message });
        }
    }
    return firstError;
}

interface Props {
    task: ProjectTask;
    users: { id: string; name: string }[];
    tasks: ProjectTask[];
    /** The current user is the project owner — they may delete tasks they cannot edit, and edit the timeline. */
    isProjectOwner?: boolean;
    isOpen?: boolean;
    onClose?: () => void;
}

function normalizeDateValue(value: string | null | undefined): string {
    if (!value) {
        return '';
    }

    const trimmed = String(value).trim();
    const match = /^(\d{4}-\d{2}-\d{2})/.exec(trimmed);
    if (match) {
        return match[1];
    }

    const parsed = new Date(trimmed);
    if (Number.isNaN(parsed.getTime())) {
        return '';
    }

    return parsed.toISOString().slice(0, 10);
}

export default function EditTaskModal({ task, users, tasks, isProjectOwner = false, isOpen: externalIsOpen, onClose: externalOnClose }: Props) {
    const { t } = useTranslations();
    const { settings } = useSettings();
    const page = usePage<{ locale?: string; auth?: { user?: { id?: string }; roles?: string[]; abilities?: string[] } }>();
    const locale = page.props.locale ?? 'en';
    const authRoles = page.props.auth?.roles ?? [];
    const authAbilities = page.props.auth?.abilities ?? [];
    const currentUserId = page.props.auth?.user?.id ?? null;
    const isAdministrator = authRoles.includes('Administrator');
    // The same rule the endpoint enforces: administrators, the department that
    // owns the task, and the person it is assigned to may edit it; the project
    // timeline is limited to administrators, sales managers and the project
    // owner. Anyone else gets the read-only sheet below instead of a form that
    // would only fail on save.
    const canEditTask = taskUpdateDenialReason(task, { roles: authRoles, abilities: authAbilities, userId: currentUserId, isProjectOwner }) === null;
    // The project owner may delete tasks in any department, but the timeline
    // task itself is never deletable.
    const canDeleteTask = task.is_project_timeline ? false : canEditTask || isProjectOwner;
    const [internalOpen, setInternalOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [serverError, setServerError] = useState<string | null>(null);
    const [timelineReasonModalOpen, setTimelineReasonModalOpen] = useState(false);
    const [timelineChangeReason, setTimelineChangeReason] = useState('');
    const [timelineReasonError, setTimelineReasonError] = useState<string | null>(null);
    const [pendingTimelineValues, setPendingTimelineValues] = useState<EditTaskFormValues | null>(null);

    const open = externalIsOpen !== undefined ? externalIsOpen : internalOpen;

    const handleOpenChange = (newOpen: boolean) => {
        if (externalOnClose && externalIsOpen !== undefined) {
            if (!newOpen) {
                externalOnClose();
            }
        } else {
            setInternalOpen(newOpen);
        }
    };

    // The stored labels come from config.json and are English only, so they go
    // through the same settings.* keys the rest of the UI uses.
    const statusOptions = settings.projectTaskStatuses.map((option) => ({
        value: option.value,
        label: settingLabel(t, 'project_task_statuses', option.value) || option.label,
    }));

    /**
     * Which department owns a task decides who may move it, so it is not a field
     * any editor should be able to reassign — only an administrator or the person
     * responsible for the project. The endpoint applies the same rule.
     */
    const canEditDepartment = isAdministrator || isProjectOwner;

    const parentTaskOptions = tasks
        .filter((t) => t.id !== task.id)
        .map((t) => ({
            value: t.id.toString(),
            label: t.name,
        }));

    const roleLtMap = useMemo(() => {
        const map = new Map<string, string>();
        (settings.projectRoleTranslations ?? []).forEach((item) => {
            if (item?.value && item?.label_lt) {
                map.set(String(item.value), String(item.label_lt));
            }
        });
        return map;
    }, [settings.projectRoleTranslations]);

    // Roles switched off in Settings → System → Projects → Task configuration.
    const hiddenRoles = useMemo(
        () =>
            new Set(
                (settings.projectRoleTranslations ?? []).filter((item) => item?.value && item.enabled === false).map((item) => String(item.value)),
            ),
        [settings.projectRoleTranslations],
    );

    const initialDepartmentKey = useMemo(() => {
        if (task.department_key) {
            return task.department_key;
        }

        if (Array.isArray(task.movable_by_roles) && task.movable_by_roles.length === 1) {
            return `role:${task.movable_by_roles[0]}`;
        }

        return '';
    }, [task.department_key, task.movable_by_roles]);

    const departmentOptions = useMemo(() => {
        const roleLabel = (roleName: string): string => {
            if (locale === 'lt') {
                return roleLtMap.get(roleName) ?? roleName;
            }
            return roleName;
        };

        // A hidden role still shows up when the task already uses it, so editing
        // another field can't silently blank the department.
        const visibleRoles = (settings.availableRoles ?? []).filter(
            (roleName) => !hiddenRoles.has(roleName) || initialDepartmentKey === `role:${roleName}`,
        );

        const roleDepartments = visibleRoles.map((roleName) => ({
            value: `role:${roleName}`,
            label: roleLabel(roleName),
            roles: [roleName],
        }));
        const customDepartments = (settings.projectTaskDepartments ?? [])
            .filter((item) => item?.value && item?.label && Array.isArray(item?.roles))
            .map((item) => ({
                value: String(item.value),
                label: String(item.label),
                roles: item.roles.filter((role): role is string => Boolean(role && typeof role === 'string')),
            }));
        return [...roleDepartments, ...customDepartments];
    }, [settings.availableRoles, settings.projectTaskDepartments, locale, roleLtMap, hiddenRoles, initialDepartmentKey]);

    const formSchema = useMemo(
        () =>
            z
                .object({
                    name: z
                        .string()
                        .min(1, t('projects.forms.edit_task.validation.name_required', 'Task name is required.'))
                        .max(
                            255,
                            t('projects.forms.task.validation.name_max', 'The task name may not be greater than :max characters.').replace(
                                ':max',
                                '255',
                            ),
                        ),
                    start_date: z.string().min(1, t('projects.forms.edit_task.validation.start_required', 'Start date is required.')),
                    end_date: z.string().min(1, t('projects.forms.edit_task.validation.end_required', 'End date is required.')),
                    status: z.string().min(1, t('projects.forms.edit_task.validation.status_required', 'Status is required.')),
                    assigned_to: z.string(),
                    parent_task_id: z.string(),
                    department_key: z.string(),
                })
                .refine((data) => data.start_date <= data.end_date, {
                    message: t('projects.forms.task.validation.end_after_or_equal', 'The end date must be on or after the start date.'),
                    path: ['end_date'],
                }),
        [t],
    );

    const form = useForm<EditTaskFormValues>({
        resolver: standardSchemaResolver(formSchema),
        defaultValues: {
            name: task.name,
            start_date: normalizeDateValue(task.start_date),
            end_date: normalizeDateValue(task.end_date),
            status: task.status,
            assigned_to: task.assigned_to?.toString() || '',
            parent_task_id: task.parent_task_id?.toString() || '',
            department_key: initialDepartmentKey,
        },
    });

    const submitTaskUpdate = (values: EditTaskFormValues, timelineReason?: string): void => {
        setServerError(null);
        form.clearErrors();

        // Only an administrator or the project's responsible person may move a task
        // between departments; for everyone else the field is not rendered and the
        // task's current department is sent back unchanged.
        const departmentKey = canEditDepartment ? values.department_key : (task.department_key ?? '');
        const selectedDepartment = departmentOptions.find((item) => item.value === departmentKey);
        const data: Record<string, unknown> = {
            ...values,
            description: task.description ?? null,
            assigned_to: values.assigned_to || null,
            parent_task_id: values.parent_task_id || null,
            department_key: selectedDepartment ? departmentKey : (task.department_key ?? null),
            movable_by_roles: selectedDepartment ? selectedDepartment.roles : (task.movable_by_roles ?? null),
        };

        if (task.is_project_timeline) {
            delete data.parent_task_id;
            delete data.assigned_to;
            delete data.status;
            if (timelineReason) {
                data.timeline_change_reason = timelineReason;
            }
        }

        router.put(route('tasks.update', task.id), data as RequestPayload, {
            preserveScroll: true,
            onStart: () => setIsSaving(true),
            onFinish: () => setIsSaving(false),
            onSuccess: () => {
                setPendingTimelineValues(null);
                setTimelineReasonModalOpen(false);
                setTimelineChangeReason('');
                setTimelineReasonError(null);
                handleOpenChange(false);
            },
            onError: (errors) => {
                const other = applyInertiaValidationErrors(errors, form.setError);
                setServerError(other);
            },
        });
    };

    function onSubmit(values: EditTaskFormValues) {
        if (task.is_project_timeline) {
            const initialStartDate = normalizeDateValue(task.start_date);
            const initialEndDate = normalizeDateValue(task.end_date);
            const dateChanged = values.start_date !== initialStartDate || values.end_date !== initialEndDate;

            if (dateChanged) {
                setPendingTimelineValues(values);
                setTimelineChangeReason('');
                setTimelineReasonError(null);
                setTimelineReasonModalOpen(true);
                return;
            }
        }

        submitTaskUpdate(values);
    }

    function onInvalid() {
        toast.error(t('projects.forms.edit_task.validation.resolve_errors', 'Please fill in all required fields.'));
    }

    const saveTimelineWithReason = (): void => {
        const reason = timelineChangeReason.trim();
        if (!reason) {
            setTimelineReasonError(
                t('projects.gantt.messages.timeline_reason_required', 'Please provide a short justification before moving the project timeline.'),
            );
            return;
        }

        if (!pendingTimelineValues) {
            setTimelineReasonModalOpen(false);
            return;
        }

        setTimelineReasonError(null);
        submitTaskUpdate(pendingTimelineValues, reason);
    };

    const cancelTimelineReasonModal = (): void => {
        setTimelineReasonModalOpen(false);
        setTimelineReasonError(null);
        setTimelineChangeReason('');
        setPendingTimelineValues(null);
    };

    function handleDelete() {
        router.delete(route('tasks.destroy', task.id), {
            onSuccess: () => {
                setDeleteDialogOpen(false);
                handleOpenChange(false);
            },
        });
    }

    const hasSubtasks = tasks.some((t) => t.parent_task_id === task.id);
    const hasDependents = tasks.some((t) => t.dependencies?.includes(task.id));

    const requiredMark = <span className="text-destructive">*</span>;

    // Read-only sheet: everything the form would show, as plain text. Some
    // labels carry a trailing colon for the detail view, which the two-line
    // layout here does not want.
    const fieldLabel = (token: string, fallback: string): string => t(token, fallback).replace(/:\s*$/, '');
    const noneLabel = t('projects.forms.edit_task.values.none', 'None');
    const readOnlyFields: { label: string; value: string }[] = [
        {
            label: fieldLabel('projects.forms.edit_task.labels.department', 'Department'),
            value: departmentOptions.find((item) => item.value === initialDepartmentKey)?.label || initialDepartmentKey || noneLabel,
        },
        {
            label: fieldLabel('projects.forms.edit_task.labels.start_date', 'Start Date'),
            value: normalizeDateValue(task.start_date) || noneLabel,
        },
        {
            label: fieldLabel('projects.forms.edit_task.labels.end_date', 'End Date'),
            value: normalizeDateValue(task.end_date) || noneLabel,
        },
    ];

    if (!task.is_project_timeline) {
        readOnlyFields.push(
            {
                label: fieldLabel('projects.forms.edit_task.labels.status', 'Status'),
                value: statusOptions.find((option) => option.value === task.status)?.label || task.status,
            },
            {
                label: fieldLabel('projects.forms.edit_task.labels.assigned_to', 'Assigned to'),
                value: users.find((user) => user.id === task.assigned_to)?.name || t('projects.forms.edit_task.values.unassigned', 'Unassigned'),
            },
            {
                label: fieldLabel('projects.forms.edit_task.labels.parent_task', 'Parent Task'),
                value: tasks.find((item) => item.id === task.parent_task_id)?.name || noneLabel,
            },
        );
    }

    return (
        <>
            <ResponsiveModal open={open} onOpenChange={handleOpenChange} variant="sheet">
                {externalIsOpen === undefined && canEditTask && (
                    <ResponsiveModalTrigger asChild>
                        <Button variant="outline" size="sm">
                            {t('projects.forms.edit_task.button', 'Edit')}
                        </Button>
                    </ResponsiveModalTrigger>
                )}
                {/* The form already owns the scrolling middle, so no ResponsiveModalBody here. */}
                <ResponsiveModalContent size="lg" drawerClassName="max-h-[min(90dvh,640px)]">
                    <ResponsiveModalHeader className="shrink-0 space-y-1 border-b px-4 py-3 text-left">
                        <ResponsiveModalTitle className="text-base">
                            {canEditTask
                                ? t('projects.forms.edit_task.title', 'Edit Task')
                                : canDeleteTask
                                  ? t('projects.forms.edit_task.delete_only_title', 'Delete Task')
                                  : t('projects.forms.edit_task.read_only_title', 'Task Details')}
                        </ResponsiveModalTitle>
                    </ResponsiveModalHeader>

                    {!canEditTask ? (
                        <div className="flex min-h-0 flex-1 flex-col">
                            <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
                                <p className="text-sm font-medium">{task.name}</p>
                                <p className="text-muted-foreground mt-2 text-sm">
                                    {canDeleteTask
                                        ? t(
                                              'projects.forms.edit_task.delete_only_description',
                                              'As the project creator you can delete this task, but editing is limited to the department that owns it.',
                                          )
                                        : t(
                                              'projects.forms.edit_task.read_only_description',
                                              'This task belongs to another department. Only that department, the person it is assigned to, or an administrator can change it.',
                                          )}
                                </p>
                                <dl className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                                    {readOnlyFields.map((field) => (
                                        <div key={field.label}>
                                            <dt className="text-muted-foreground text-xs">{field.label}</dt>
                                            <dd className="mt-0.5 text-sm break-words">{field.value}</dd>
                                        </div>
                                    ))}
                                </dl>
                                {task.description ? (
                                    <dl className="mt-3">
                                        <dt className="text-muted-foreground text-xs">
                                            {fieldLabel('projects.forms.edit_task.labels.description', 'Description')}
                                        </dt>
                                        <dd className="mt-0.5 text-sm whitespace-pre-line">{task.description}</dd>
                                    </dl>
                                ) : null}
                            </div>
                            <div className="flex shrink-0 flex-row items-center justify-end gap-2 border-t px-4 py-3">
                                <Button type="button" variant="outline" size="sm" onClick={() => handleOpenChange(false)}>
                                    {t('projects.forms.edit_task.buttons.cancel', 'Cancel')}
                                </Button>
                                {canDeleteTask ? (
                                    <Button type="button" variant="destructive" size="sm" onClick={() => setDeleteDialogOpen(true)}>
                                        <Trash2 className="mr-2 h-4 w-4" aria-hidden />
                                        {t('projects.forms.edit_task.delete_dialog.delete_task', 'Delete')}
                                    </Button>
                                ) : null}
                            </div>
                        </div>
                    ) : (
                        <Form {...form}>
                            <form onSubmit={form.handleSubmit(onSubmit, onInvalid)} className="flex min-h-0 flex-1 flex-col" noValidate>
                                <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
                                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-x-3 sm:gap-y-3">
                                        {serverError ? (
                                            <Alert variant="destructive" className="sm:col-span-2">
                                                <AlertDescription>{serverError}</AlertDescription>
                                            </Alert>
                                        ) : null}
                                        <FormField
                                            control={form.control}
                                            name="name"
                                            render={({ field }) => (
                                                <FormItem className="sm:col-span-2">
                                                    <FormLabel className="text-xs">
                                                        {t('projects.forms.edit_task.labels.task_name', 'Task Name')} {requiredMark}
                                                    </FormLabel>
                                                    <FormControl>
                                                        <Input {...field} className="h-9" disabled={task.is_project_timeline} />
                                                    </FormControl>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />

                                        <FormField
                                            control={form.control}
                                            name="start_date"
                                            render={({ field }) => (
                                                <FormItem>
                                                    <FormLabel className="text-xs">
                                                        {t('projects.forms.edit_task.labels.start_date', 'Start Date')} {requiredMark}
                                                    </FormLabel>
                                                    <FormControl>
                                                        <DatePicker
                                                            value={field.value}
                                                            onValueChange={field.onChange}
                                                            placeholder={t(
                                                                'projects.forms.edit_task.placeholders.select_start_date',
                                                                'Select start date',
                                                            )}
                                                            className="h-9 w-full"
                                                        />
                                                    </FormControl>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />

                                        <FormField
                                            control={form.control}
                                            name="end_date"
                                            render={({ field }) => (
                                                <FormItem>
                                                    <FormLabel className="text-xs">
                                                        {t('projects.forms.edit_task.labels.end_date', 'End Date')} {requiredMark}
                                                    </FormLabel>
                                                    <FormControl>
                                                        <DatePicker
                                                            value={field.value}
                                                            onValueChange={field.onChange}
                                                            placeholder={t(
                                                                'projects.forms.edit_task.placeholders.select_end_date',
                                                                'Select end date',
                                                            )}
                                                            className="h-9 w-full"
                                                        />
                                                    </FormControl>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />

                                        {canEditDepartment ? (
                                            <FormField
                                                control={form.control}
                                                name="department_key"
                                                render={({ field }) => (
                                                    <FormItem className="sm:col-span-2">
                                                        <FormLabel className="text-xs">
                                                            {t('projects.forms.edit_task.labels.department', 'Department')}
                                                        </FormLabel>
                                                        <FormControl>
                                                            <Combobox
                                                                options={departmentOptions.map((item) => ({ value: item.value, label: item.label }))}
                                                                value={field.value}
                                                                onValueChange={field.onChange}
                                                                placeholder={t(
                                                                    'projects.forms.edit_task.placeholders.select_department',
                                                                    'Select department',
                                                                )}
                                                                searchPlaceholder={t(
                                                                    'projects.forms.edit_task.placeholders.search_department',
                                                                    'Search department...',
                                                                )}
                                                                className="h-9 w-full"
                                                            />
                                                        </FormControl>
                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />
                                        ) : null}

                                        {!task.is_project_timeline ? (
                                            <FormField
                                                control={form.control}
                                                name="status"
                                                render={({ field }) => (
                                                    <FormItem className="sm:col-span-2">
                                                        <FormLabel className="text-xs">
                                                            {t('projects.forms.edit_task.labels.status', 'Status')} {requiredMark}
                                                        </FormLabel>
                                                        <FormControl>
                                                            <Combobox
                                                                options={statusOptions}
                                                                value={field.value}
                                                                onValueChange={field.onChange}
                                                                placeholder={t(
                                                                    'projects.forms.edit_task.placeholders.select_status',
                                                                    'Select status',
                                                                )}
                                                                searchPlaceholder={t(
                                                                    'projects.forms.edit_task.placeholders.search_status',
                                                                    'Search status...',
                                                                )}
                                                                className="h-9 w-full"
                                                            />
                                                        </FormControl>
                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />
                                        ) : null}

                                        {!task.is_project_timeline ? (
                                            <FormField
                                                control={form.control}
                                                name="assigned_to"
                                                render={({ field }) => (
                                                    <FormItem className="sm:col-span-2">
                                                        <FormLabel className="text-xs">
                                                            {t('projects.forms.edit_task.labels.assigned_to', 'Assigned To')}
                                                        </FormLabel>
                                                        <FormControl>
                                                            <UserSelect
                                                                value={field.value || null}
                                                                onValueChange={(v) => field.onChange(v ?? '')}
                                                                seedUsers={users.map((u) => {
                                                                    const [first, ...rest] = (u.name || '').split(' ');
                                                                    return { id: u.id, first_name: first || u.name, last_name: rest.join(' ') };
                                                                })}
                                                                placeholder={t(
                                                                    'projects.forms.edit_task.placeholders.select_assignee',
                                                                    'Select assignee',
                                                                )}
                                                                allowClear
                                                                className="h-9 w-full"
                                                            />
                                                        </FormControl>
                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />
                                        ) : null}

                                        {!task.is_project_timeline ? (
                                            <FormField
                                                control={form.control}
                                                name="parent_task_id"
                                                render={({ field }) => (
                                                    <FormItem className="sm:col-span-2">
                                                        <FormLabel className="text-xs">
                                                            {t('projects.forms.edit_task.labels.parent_task', 'Parent Task')}
                                                        </FormLabel>
                                                        <FormControl>
                                                            <Combobox
                                                                options={parentTaskOptions}
                                                                value={field.value}
                                                                onValueChange={field.onChange}
                                                                placeholder={t(
                                                                    'projects.forms.edit_task.placeholders.select_parent_task',
                                                                    'Select parent task',
                                                                )}
                                                                searchPlaceholder={t(
                                                                    'projects.forms.edit_task.placeholders.search_tasks',
                                                                    'Search tasks...',
                                                                )}
                                                                allowClear={true}
                                                                clearText={t(
                                                                    'projects.forms.edit_task.placeholders.no_parent_task',
                                                                    'No parent task',
                                                                )}
                                                                className="h-9 w-full"
                                                            />
                                                        </FormControl>
                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />
                                        ) : null}
                                    </div>
                                </div>

                                <div className="flex shrink-0 flex-row flex-wrap items-center justify-between gap-2 border-t px-4 py-3">
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                                        onClick={() => setDeleteDialogOpen(true)}
                                        aria-label={t('projects.forms.edit_task.delete_aria', 'Delete task')}
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </Button>
                                    <div className="flex flex-1 justify-end gap-2 sm:flex-initial">
                                        <Button type="button" variant="outline" size="sm" onClick={() => handleOpenChange(false)}>
                                            {t('projects.forms.edit_task.buttons.cancel', 'Cancel')}
                                        </Button>
                                        <Button type="submit" size="sm" disabled={isSaving}>
                                            {isSaving ? (
                                                <>
                                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
                                                    {t('common.saving', 'Saving...')}
                                                </>
                                            ) : (
                                                t('projects.forms.edit_task.buttons.save_changes', 'Save')
                                            )}
                                        </Button>
                                    </div>
                                </div>
                            </form>
                        </Form>
                    )}
                </ResponsiveModalContent>
            </ResponsiveModal>

            <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t('projects.forms.edit_task.delete_dialog.title', 'Delete task?')}</AlertDialogTitle>
                        <AlertDialogDescription asChild>
                            <div className="space-y-2 text-left">
                                <p>
                                    {t(
                                        'projects.forms.edit_task.delete_dialog.description',
                                        'Are you sure you want to delete ":name"? This cannot be undone.',
                                    ).replace(':name', task.name)}
                                </p>
                                {hasSubtasks && (
                                    <p className="text-warning-subtle-foreground text-sm font-medium">
                                        {t(
                                            'projects.forms.edit_task.delete_dialog.has_subtasks_warning',
                                            'Warning: This task has subtasks. They will become top-level tasks after deletion.',
                                        )}
                                    </p>
                                )}
                                {hasDependents && (
                                    <p className="text-warning-subtle-foreground text-sm font-medium">
                                        {t(
                                            'projects.forms.edit_task.delete_dialog.has_dependencies_warning',
                                            'Warning: Other tasks depend on this task. Those dependencies will be removed.',
                                        )}
                                    </p>
                                )}
                            </div>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>{t('projects.forms.edit_task.delete_dialog.cancel', 'Cancel')}</AlertDialogCancel>
                        <Button type="button" variant="destructive" onClick={handleDelete}>
                            {t('projects.forms.edit_task.delete_dialog.delete_task', 'Delete')}
                        </Button>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <ResponsiveModal
                open={timelineReasonModalOpen}
                onOpenChange={(nextOpen) => (nextOpen ? setTimelineReasonModalOpen(true) : cancelTimelineReasonModal())}
                variant="sheet"
            >
                <ResponsiveModalContent size="md">
                    <ResponsiveModalHeader>
                        <ResponsiveModalTitle>{t('projects.gantt.timeline_reason.title', 'Project timeline justification')}</ResponsiveModalTitle>
                        <ResponsiveModalDescription>
                            {t('projects.gantt.timeline_reason.description', 'Please explain why project timeline dates were changed.')}
                        </ResponsiveModalDescription>
                    </ResponsiveModalHeader>
                    <ResponsiveModalBody className="space-y-2">
                        <Input
                            value={timelineChangeReason}
                            onChange={(event) => {
                                setTimelineChangeReason(event.target.value);
                                if (timelineReasonError) {
                                    setTimelineReasonError(null);
                                }
                            }}
                            placeholder={t('projects.gantt.timeline_reason.placeholder', 'Short justification')}
                        />
                        {timelineReasonError ? <p className="text-destructive text-sm">{timelineReasonError}</p> : null}
                    </ResponsiveModalBody>
                    <ResponsiveModalFooter>
                        <Button type="button" variant="outline" onClick={cancelTimelineReasonModal}>
                            {t('common.cancel', 'Cancel')}
                        </Button>
                        <Button type="button" onClick={saveTimelineWithReason}>
                            {t('common.save', 'Save')}
                        </Button>
                    </ResponsiveModalFooter>
                </ResponsiveModalContent>
            </ResponsiveModal>
        </>
    );
}
